<template>
  <div class="enhanced-building-table">
    <!-- Filters Section -->
    <div class="filters-section mb-4">
      <div class="row">
        <div class="col-md-4">
          <b-form-group label="Search" label-for="search-input">
            <b-form-input
              id="search-input"
              v-model="filters.search"
              placeholder="Search buildings, customers, creators..."
              @input="debouncedSearch"
            />
          </b-form-group>
        </div>
        <div class="col-md-2">
          <b-form-group label="Type" label-for="type-select">
            <b-form-select
              id="type-select"
              v-model="filters.type"
              :options="typeOptions"
              @change="onFilterChange"
            />
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="Date Range" label-for="date-range">
            <div class="d-flex">
              <b-form-input
                v-model="filters.startDate"
                type="date"
                class="mr-2"
                @change="onFilterChange"
              />
              <b-form-input
                v-model="filters.endDate"
                type="date"
                @change="onFilterChange"
              />
            </div>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="Sort By" label-for="sort-select">
            <div class="d-flex">
              <b-form-select
                id="sort-select"
                v-model="filters.sortBy"
                :options="sortOptions"
                class="mr-2"
                @change="onFilterChange"
              />
              <b-form-select
                v-model="filters.sortOrder"
                :options="sortOrderOptions"
                @change="onFilterChange"
              />
            </div>
          </b-form-group>
        </div>
      </div>
      
      <!-- Advanced Filters (Collapsible) -->
      <b-button
        v-b-toggle.advanced-filters
        variant="outline-secondary"
        size="sm"
        class="mb-3"
      >
        <i class="fa fa-cog"></i> Advanced Filters
      </b-button>
      
      <b-collapse id="advanced-filters">
        <div class="advanced-filters-content p-3 border rounded mb-3">
          <div class="row">
            <div class="col-md-3">
              <b-form-group label="Min Width (ft)">
                <b-form-input
                  v-model="filters.minWidth"
                  type="number"
                  placeholder="0"
                  @change="onFilterChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-3">
              <b-form-group label="Max Width (ft)">
                <b-form-input
                  v-model="filters.maxWidth"
                  type="number"
                  placeholder="∞"
                  @change="onFilterChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-3">
              <b-form-group label="Min Length (ft)">
                <b-form-input
                  v-model="filters.minLength"
                  type="number"
                  placeholder="0"
                  @change="onFilterChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-3">
              <b-form-group label="Max Length (ft)">
                <b-form-input
                  v-model="filters.maxLength"
                  type="number"
                  placeholder="∞"
                  @change="onFilterChange"
                />
              </b-form-group>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Created By">
                <b-form-select
                  v-model="filters.createdBy"
                  :options="createdByOptions"
                  @change="onFilterChange"
                >
                  <template #first>
                    <b-form-select-option value="">All Creators</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Customer Email">
                <b-form-input
                  v-model="filters.email"
                  placeholder="Filter by customer email"
                  @input="debouncedSearch"
                />
              </b-form-group>
            </div>
          </div>
          <b-button @click="clearFilters" variant="outline-danger" size="sm">
            Clear All Filters
          </b-button>
        </div>
      </b-collapse>
    </div>

    <!-- Results Info -->
    <div class="results-info mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <span class="text-muted">
            Showing {{ buildings.length }} of {{ totalCount }} buildings
          </span>
        </div>
        <div>
          <b-form-group label="Per Page" label-cols="auto" class="mb-0">
            <b-form-select
              v-model="filters.limit"
              :options="limitOptions"
              size="sm"
              @change="onFilterChange"
            />
          </b-form-group>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <circle-loader :size="40"></circle-loader>
    </div>

    <!-- Table -->
    <div v-else-if="buildings.length" class="table-responsive">
      <b-table
        :items="buildingList"
        :fields="fields"
        striped
        hover
        @row-clicked="openBuilding"
        :busy="loading"
        show-empty
        empty-text="No buildings found"
      >
        <template #cell(customerName)="data">
          <strong>{{ data.value }}</strong>
        </template>
        
        <template #cell(dimensions)="data">
          <span class="badge badge-light">{{ data.value }}</span>
        </template>
        
        <template #cell(type)="data">
          <span 
            class="badge"
            :class="getTypeBadgeClass(data.value)"
          >
            {{ data.value }}
          </span>
        </template>
        
        <template #cell(createdAt)="data">
          <small class="text-muted">{{ data.value }}</small>
        </template>

        <template #cell(actions)="data">
          <b-button-group size="sm">
            <b-button 
              variant="outline-primary" 
              @click.stop="openBuilding(data.item)"
              title="Edit Building"
            >
              <i class="fa fa-edit"></i>
            </b-button>
            <b-button 
              variant="outline-info" 
              @click.stop="viewBuilding(data.item)"
              title="View Details"
            >
              <i class="fa fa-eye"></i>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </div>

    <!-- No Results -->
    <div v-else-if="!loading" class="text-center py-5">
      <div class="text-muted">
        <i class="fa fa-search fa-3x mb-3"></i>
        <h5>No buildings found</h5>
        <p>Try adjusting your search filters</p>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalCount > filters.limit" class="d-flex justify-content-center mt-4">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalCount"
        :per-page="filters.limit"
        @change="onPageChange"
        align="center"
        size="sm"
      />
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore && !loading" class="text-center mt-3">
      <b-button @click="loadMore" variant="outline-primary">
        Load More Buildings
      </b-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import CircleLoader from '@/components/CircleLoader'

export default {
  name: 'EnhancedBuildingTable',
  components: { CircleLoader },
  props: {
    apiEndpoint: {
      type: String,
      required: true
    },
    showCreatorColumn: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      buildings: [],
      totalCount: 0,
      hasMore: false,
      loading: false,
      loadingUsers: false,
      currentPage: 1,
      users: [],
      filters: {
        search: '',
        type: 'all',
        startDate: '',
        endDate: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 25,
        minWidth: '',
        maxWidth: '',
        minLength: '',
        maxLength: '',
        createdBy: '',
        email: ''
      },
      typeOptions: [
        { value: 'all', text: 'All Types' },
        { value: 'Carport', text: 'Carport' },
        { value: 'Shop', text: 'Shop' },
        { value: 'RV Cover', text: 'RV Cover' },
        { value: 'Shed', text: 'Shed' },
        { value: 'Residential', text: 'Residential' }
      ],
      sortOptions: [
        { value: 'createdAt', text: 'Created Date' },
        { value: 'updatedAt', text: 'Updated Date' },
        { value: 'customer.firstName', text: 'Customer Name' },
        { value: 'type', text: 'Type' },
        { value: 'dimensions.sqft', text: 'Square Feet' }
      ],
      sortOrderOptions: [
        { value: 'desc', text: 'Descending' },
        { value: 'asc', text: 'Ascending' }
      ],
      limitOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ]
    }
  },
  computed: {
    fields() {
      const baseFields = [
        {
          key: 'customerName',
          label: 'Customer',
          sortable: true,
          class: 'clickable-row'
        },
        {
          key: 'customerEmail',
          label: 'Email',
          sortable: true,
          class: 'clickable-row'
        },
        {
          key: 'dimensions',
          label: 'Dimensions',
          sortable: false,
          class: 'clickable-row'
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true,
          class: 'clickable-row'
        },
        {
          key: 'nickname',
          label: 'Nickname',
          sortable: true,
          class: 'clickable-row'
        }
      ]

      if (this.showCreatorColumn) {
        baseFields.push({
          key: 'createdBy',
          label: 'Creator',
          sortable: true,
          class: 'clickable-row'
        })
      }

      baseFields.push(
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'clickable-row'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false
        }
      )

      return baseFields
    },
    buildingList() {
      return this.buildings.map(building => {
        let customerName = building.customer.name ||
          (building.customer.firstName || '') + ' ' + (building.customer.lastName || '')
        
        return {
          shortId: building.shortId,
          customerName: customerName.trim(),
          customerEmail: building.customer.email,
          nickname: building.nickname,
          dimensions: this.formatDimensions(building.dimensions),
          type: building.type,
          createdBy: building.createdBy,
          createdAt: this.$options.filters.shortFullDateFilter(building.createdAt),
          _original: building
        }
      })
    },
    createdByOptions() {
      return this.users.map(user => ({
        value: user.value,
        text: user.text
      }))
    }
  },
  methods: {
    async loadBuildings(resetData = true) {
      if (this.loading) return
      
      this.loading = true
      
      try {
        const params = new URLSearchParams()
        
        // Add all filters to params
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== '' && this.filters[key] !== 'all') {
            params.append(key, this.filters[key])
          }
        })
        
        // Add pagination
        if (!resetData) {
          params.append('skip', this.buildings.length)
        }
        
        const response = await axios.get(`${this.apiEndpoint}?${params.toString()}`)
        
        if (resetData) {
          this.buildings = response.data.buildings
        } else {
          this.buildings.push(...response.data.buildings)
        }
        
        this.totalCount = response.data.totalCount
        this.hasMore = response.data.hasMore
        
      } catch (error) {
        console.error('Error loading buildings:', error)
        this.$bvToast.toast('Error loading buildings', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    
    onFilterChange() {
      this.currentPage = 1
      this.loadBuildings(true)
    },
    
    onPageChange(page) {
      this.currentPage = page
      const skip = (page - 1) * this.filters.limit
      this.filters.skip = skip
      this.loadBuildings(true)
    },
    
    loadMore() {
      this.loadBuildings(false)
    },
    
    clearFilters() {
      this.filters = {
        search: '',
        type: 'all',
        startDate: '',
        endDate: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 25,
        minWidth: '',
        maxWidth: '',
        minLength: '',
        maxLength: '',
        createdBy: '',
        email: ''
      }
      this.onFilterChange()
    },
    
    formatDimensions(dimensions) {
      return `${dimensions.width}×${dimensions.length}×${dimensions.height}`
    },
    
    getTypeBadgeClass(type) {
      const classes = {
        'Carport': 'badge-primary',
        'Shop': 'badge-success',
        'RV Cover': 'badge-info',
        'Shed': 'badge-warning',
        'Residential': 'badge-secondary'
      }
      return classes[type] || 'badge-light'
    },
    
    openBuilding(building) {
      const buildingData = building._original || building
      if (event && event.metaKey) {
        window.open(`/app/pricer/${buildingData.shortId}`, '_blank')
      } else {
        this.$router.push(`/app/pricer/${buildingData.shortId}`)
      }
    },
    
    viewBuilding(building) {
      const buildingData = building._original || building
      // Could open a modal or navigate to a detail view
      this.$bvToast.toast(`Viewing building ${buildingData.shortId}`, {
        title: 'Building Details',
        variant: 'info'
      })
    },
    
    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await axios.get('/services/v2/users/list')
        this.users = response.data.users
      } catch (error) {
        console.error('Error loading users:', error)
        this.$bvToast.toast('Error loading users for filter', {
          title: 'Error',
          variant: 'warning'
        })
      } finally {
        this.loadingUsers = false
      }
    }
  },
  async created() {
    // Create debounced search function
    this.debouncedSearch = _.debounce(this.onFilterChange, 500)
    
    // Load users for dropdown and initial buildings data
    await Promise.all([
      this.loadUsers(),
      this.loadBuildings()
    ])
  }
}
</script>

<style lang="scss" scoped>
.enhanced-building-table {
  .filters-section {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1.5rem;
  }

  .advanced-filters-content {
    background: #ffffff;
    border-color: #dee2e6 !important;
  }

  .results-info {
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 0.75rem;
  }

  .clickable-row {
    cursor: pointer;
  }

  .table-responsive {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
  }

  .badge {
    font-size: 0.75rem;
  }

  .fa-3x {
    opacity: 0.3;
  }
}

// Bootstrap table hover effects
::v-deep .table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.075);
}

::v-deep .clickable-row {
  cursor: pointer;
}
</style>
